import {
  DateTime,
} from 'luxon';
import { DateLocalizer } from 'react-big-calendar/lib/localizer';
import * as dates from 'react-big-calendar/lib/utils/dates';

interface DateRange {
  start: Date;
  end: Date;
}

type Culture = string | undefined;
type Localizer = {
  format: (date: Date, format: string, culture?: Culture) => string;
};

// Helper function for date range formatting
const formatRange = (
  start: Date,
  end: Date,
  formatStr: string,
  culture: Culture,
  local: Localizer,
): string => (
  `${local.format(start, formatStr, culture)} — ${local.format(
    end,
    formatStr,
    culture,
  )}`);

// Specific date and time formatting functions
const dateRangeFormat = (
  range: DateRange,
  culture: Culture,
  local: Localizer,
): string => formatRange(range.start, range.end, 'D', culture, local);

const timeRangeFormat = (
  range: DateRange,
  culture: Culture,
  local: Localizer,
): string => formatRange(range.start, range.end, 't', culture, local);

const timeRangeStartFormat = (
  { start }: { start: Date },
  culture: Culture,
  local: Localizer,
): string => `${local.format(start, 't', culture)} —`;

const timeRangeEndFormat = (
  { end }: { end: Date },
  culture: Culture,
  local: Localizer,
): string => `— ${local.format(end, 't', culture)}`;

const weekRangeFormat = (
  { start, end }: DateRange,
  culture: Culture,
  local: Localizer,
): string => {
  const startFormat = 'MMMM dd';
  const endFormat = dates.eq(start, end, 'month') ? 'dd' : 'MMMM dd';
  return `${local.format(start, startFormat, culture)} - ${local.format(
    end,
    endFormat,
    culture,
  )}`;
};

// Define formats for different date and time displays
export const formats = {
  dateFormat: 'dd',
  dayFormat: 'dd EEE',
  weekdayFormat: 'ccc',
  selectRangeFormat: timeRangeFormat,
  eventTimeRangeFormat: timeRangeFormat,
  eventTimeRangeStartFormat: timeRangeStartFormat,
  eventTimeRangeEndFormat: timeRangeEndFormat,
  timeGutterFormat: 't',
  monthHeaderFormat: 'MMMM yyyy',
  dayHeaderFormat: 'cccc MMM dd',
  dayRangeHeaderFormat: weekRangeFormat,
  agendaHeaderFormat: dateRangeFormat,
  agendaDateFormat: 'EEE MMM dd',
  agendaTimeFormat: 't',
  agendaTimeRangeFormat: timeRangeFormat,
};

// Date localizer function
export default function createDateLocalizer(
  { firstDayOfWeek }: { firstDayOfWeek: number },
): DateLocalizer {
  const getLocale = (dateTimeInstance: any, culture: Culture) => (culture
    ? dateTimeInstance.reconfigure(culture)
    : dateTimeInstance);

  return new DateLocalizer({
    formats,
    firstOfWeek() {
      return firstDayOfWeek;
    },
    parse(value: string, format: string, culture: Culture) {
      return getLocale(DateTime.fromFormat(value, format), culture).toJSDate();
    },
    format(value: Date, format: string, culture: Culture) {
      return getLocale(DateTime.fromJSDate(value), culture).toFormat(format);
    },
  });
}
